import { PageOptions } from '@graphcommerce/framer-next-pages'
import { hygraphPageContent, HygraphPagesQuery } from '@graphcommerce/graphcms-ui'
import { StoreConfigDocument } from '@graphcommerce/magento-store'
import { PageMeta, BlogTitle, GetStaticProps, Row, LayoutTitle } from '@graphcommerce/next-ui'
import { Box } from '@mui/material'
import { GetStaticPaths } from 'next'
import {
  BlogHeader,
  BlogListDocument,
  BlogListQuery,
  BlogPostPathsDocument,
  RowRenderer,
  LayoutNavigation,
  LayoutNavigationProps,
  BlogTags,
  BlogList,
} from '../../components'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { LayoutHeader } from '../../components/Layout/components/LayoutHeader'
import { BlogRowProductDocument, BlogRowProductQuery } from '../../graphql/BlogRowProduct.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'

type Props = HygraphPagesQuery & BlogListQuery & BlogRowProductQuery
type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

type GetProductKeysProps = {
  items: NonNullable<BlogRowProductQuery['products']>['items']
  urlKeys: string[]
}

function getProductKeys(props: GetProductKeysProps) {
  const { items, urlKeys } = props
  return items?.filter((item) =>
    item?.url_key ? urlKeys.some((key) => key === item.url_key) : false,
  )
}

function BlogPage(props: Props) {
  const { pages, products, blogPosts } = props
  const defaultPage = pages[0]
  const title = defaultPage.title ?? ''

  const filteredProducts = products?.items?.filter((item) => item?.stock_status === 'IN_STOCK')

  const page: NonNullable<typeof pages>[0] = {
    ...defaultPage,
    content: [
      ...defaultPage.content.map((c) =>
        c.__typename === 'RowProduct'
          ? {
              ...c,
              items: getProductKeys({ items: filteredProducts, urlKeys: c.productUrlKeys }),
            }
          : c,
      ),
    ],
  }

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.primary.contrastText,
        py: '1px',
        '& .MuiContainer-maxWidthLg': {
          marginBottom: theme.spacings.xl,
        },
        '& .MuiTypography-root': {
          marginBottom: theme.spacings.xxs,
        },
        '& .ColumnTwo-root, .ColumnThree-root, p.MuiTypography-root': {
          padding: 0,
          marginTop: 0,
          marginBottom: theme.spacings.sm,
        },
        '& .ColumnTwo-root, .ColumnThree-root': {
          gridRowGap: theme.spacings.sm,
          gridColumnGap: theme.spacings.sm,
        },
        '& .ColumnTwo-root p:last-of-type, .ColumnThree-root p:last-of-type, h1.MuiTypography-root':
          {
            marginBottom: 0,
          },
        '& .ColumnThree-root > .MuiBox-root, .ColumnTwo-root': {
          background: '#FEF6F6',
          borderRadius: '10px',
          padding: theme.spacings.xxs,
        },
        '& .cta-btn': {
          background: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: '10px',
          padding: `${theme.spacings.xxs} ${theme.spacings.sm}`,
          textTransform: 'uppercase',
          display: 'inline-table',
          fontWeight: '600',
        },
        '& .cta-btn:hover': {
          background: theme.palette.primary.contrastText,
          color: theme.palette.primary.main,
        },
      })}
    >
      <LayoutHeader floatingMd>
        <LayoutTitle size='small' component='span'>
          {title}
        </LayoutTitle>
      </LayoutHeader>
      <Row
        maxWidth='md'
        sx={(theme) => ({
          '& .BlogHeader-header': {
            maxWidth: theme.breakpoints.values.md,
            marginBottom: theme.spacings.sm,
          },
          '& .BlogHeader-header img': {
            borderRadius: '10px',
          },
          '& video': {
            width: '100%',
          },
        })}
      >
        <PageMeta title={title} metaDescription={title} canonical={`/${page.url}`} />
        <BlogTitle
          sx={(theme) => ({
            maxWidth: theme.breakpoints.values.md,
            paddingRight: { md: `calc(${theme.spacings.xxl} * 1.2)` },
            '& .LayoutTitle-root': {
              marginBottom: theme.spacings.sm,
              alignItems: 'inherit',
            },
          })}
        >
          {title}
        </BlogTitle>
        {page.asset ? <BlogHeader asset={page.asset} /> : null}
        <RowRenderer
          {...page}
          sx={(theme) => ({
            backgroundColor: theme.palette.primary.light,
            padding: theme.spacings.sm,
            marginBottom: theme.spacings.sm,
            borderRadius: '10px',
            '& > div': {
              gridTemplateColumns: '1fr',
              '& > div:first-child': { display: 'none' },
            },
            '& .Scroller-root': {
              paddingRight: 0,
            },
          })}
        />

        {page.relatedPages.length > 0 && (
          <Box sx={(theme) => ({ mt: theme.spacings.md, mb: `calc(${theme.spacings.lg} * -1)` })}>
            <BlogTags relatedPages={page.relatedPages} />
          </Box>
        )}
      </Row>
      <BlogList blogPosts={blogPosts} promotionItems={[]} />
    </Box>
  )
}

BlogPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default BlogPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (import.meta.graphCommerce.limitSsg) return { paths: [], fallback: 'blocking' }

  const responses = locales.map(async (locale) => {
    const staticClient = graphqlSsrClient(locale)
    const BlogPostPaths = staticClient.query({ query: BlogPostPathsDocument })
    const { pages } = (await BlogPostPaths).data
    return (
      pages.map((page) => ({ params: { url: `${page?.url}`.replace('blog/', '') }, locale })) ?? []
    )
  })
  const paths = (await Promise.all(responses)).flat(1)
  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ locale, params }) => {
  const urlKey = params?.url ?? '??'
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)
  const limit = 3
  const conf = client.query({ query: StoreConfigDocument })
  const page = hygraphPageContent(staticClient, `blog/${urlKey}`)

  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const blogPosts = staticClient.query({
    query: BlogListDocument,
    variables: {
      currentUrl: [`blog/${urlKey}`],
      first: limit,
    },
  })

  const url_keys = (await page).data.pages[0]?.content
    .map((c) => (c.__typename === 'RowProduct' ? c.productUrlKeys : []))
    .flat(1)

  const blogProducts = url_keys
    ? (await staticClient.query({ query: BlogRowProductDocument, variables: { url_keys } })).data
    : ({ products: [] } as BlogRowProductQuery)

  if (!(await page).data.pages?.[0]) return { notFound: true }

  return {
    props: {
      ...(await page).data,
      ...(await blogPosts).data,
      ...(await layout).data,
      ...blogProducts,
      up: { href: '/', title: 'Home' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
